<script setup>
import { onBeforeUnmount, onBeforeMount, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { gql } from "@apollo/client/core";
import apolloClient from "@/apollo";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSwitch from "@/components/ArgonSwitch.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const body = document.getElementsByTagName("body")[0];
const store = useStore();
const router = useRouter();

// Form data
const email = ref("");
const password = ref("");
const rememberMe = ref(false);
const loading = ref(false);
const errorMessage = ref("");

// GraphQL Login Mutation
const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(loginUserInput: { email: $email, password: $password }) {
      user {
        firstName
        lastName
      }
      accessToken
    }
  }
`;

// Handle login
const handleLogin = async () => {
  // Clear previous errors
  errorMessage.value = "";

  // Basic validation
  if (!email.value || !password.value) {
    errorMessage.value = "Veuillez remplir tous les champs";
    return;
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    errorMessage.value = "Veuillez entrer une adresse email valide";
    return;
  }

  loading.value = true;

  try {
    const { data } = await apolloClient.mutate({
      mutation: LOGIN_MUTATION,
      variables: {
        email: email.value,
        password: password.value,
      },
    });

    if (data?.login) {
      // Store the access token in cookie with expiration (1 hour)
      const token = data.login.accessToken;
      const expirationHours = 1;
      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + (expirationHours * 60 * 60 * 1000));
      document.cookie = `accessToken=${token}; expires=${expirationDate.toUTCString()}; path=/`;

      // Store user info if needed
      if (data.login.user) {
        localStorage.setItem("user", JSON.stringify(data.login.user));
      }

      // Optionally store in Vuex
      store.state.user = data.login.user;
      store.state.isAuthenticated = true;

      // Redirect to dashboard
      router.push("/tableau-de-bord");
    }
  } catch (error) {
    console.error("Login error:", error);
    
    // Handle different error types
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      errorMessage.value = error.graphQLErrors[0].message || "Erreur de connexion";
    } else if (error.networkError) {
      errorMessage.value = "Erreur de connexion au serveur";
    } else {
      errorMessage.value = "Email ou mot de passe incorrect";
    }
  } finally {
    loading.value = false;
  }
};

onBeforeMount(() => {
  store.state.hideConfigButton = true;
  store.state.showNavbar = false;
  store.state.showSidenav = false;
  store.state.showFooter = false;
  body.classList.remove("bg-gray-100");
});

onBeforeUnmount(() => {
  store.state.hideConfigButton = false;
  store.state.showNavbar = true;
  store.state.showSidenav = true;
  store.state.showFooter = true;
  body.classList.add("bg-gray-100");
});
</script>
<template>
  <div class="container top-0 position-sticky z-index-sticky">
    <div class="row">
   
    </div>
  </div>
  <main class="mt-0 main-content">
    <section>
      <div class="page-header min-vh-100">
        <div class="container">
          <div class="row">
            <div
              class="mx-auto col-xl-4 col-lg-5 col-md-7 d-flex flex-column mx-lg-0"
            >
              <div class="card card-plain">
                <div class="pb-0 card-header text-start">
                  <h4 class="font-weight-bolder">Connexion</h4>
                 </div>
                <div class="card-body">
                  <form role="form" @submit.prevent="handleLogin">
                    <!-- Error Message -->
                    <div v-if="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
                      <span class="alert-text text-white">{{ errorMessage }}</span>
                      <button type="button" class="btn-close" @click="errorMessage = ''" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>

                    <div class="mb-3">
                      <argon-input
                        id="email"
                        v-model="email"
                        type="email"
                        placeholder="Email"
                        name="email"
                        size="lg"
                        :disabled="loading"
                      />
                    </div>
                    <div class="mb-3">
                      <argon-input
                        id="password"
                        v-model="password"
                        type="password"
                        placeholder="Password"
                        name="password"
                        size="lg"
                        :disabled="loading"
                      />
                    </div>
                    <argon-switch
                      id="rememberMe"
                      v-model="rememberMe"
                      name="remember-me"
                      :disabled="loading"
                    >
                      Se souvenir la connexion
                    </argon-switch>

                    <div class="text-center">
                      <argon-button
                        type="submit"
                        class="mt-4"
                        variant="gradient"
                        color="success"
                        fullWidth
                        size="lg"
                        :disabled="loading"
                      >
                        <span v-if="loading">
                          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Connexion en cours...
                        </span>
                        <span v-else>Se connecter</span>
                      </argon-button>
                    </div>
                  </form>
                </div>
                 
              </div>
            </div>
            <div
              class="top-0 my-auto text-center col-6 d-lg-flex d-none h-100 pe-0 position-absolute end-0 justify-content-center flex-column"
            >
              <div
                class="position-relative bg-gradient-primary h-100 m-3 px-7 border-radius-lg d-flex flex-column justify-content-center overflow-hidden"
              
              >
                <span class="mask bg-gradient-success opacity-10"></span>
                <h4
                  class="mt-5 text-white font-weight-bolder position-relative"
                >
                   Bienvenue dans votre espace Admin 
                </h4>
               
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>
